import { safeFileName } from "./slug";
import { fileKey } from "./resume";
import type { DataKind, FileTask, TeamForm } from "./types";

/** Drone stills only. DJI thermal R-JPEGs also carry a .jpg extension. */
const ACCEPTED = /\.(jpe?g|png|tiff?|dng)$/i;

/**
 * DJI writes a variant letter before the extension: _T for the thermal frame,
 * _V / _W / _Z for visible, wide and zoom. A plain DJI_0001.JPG is RGB.
 *
 * This matters because an M30T writes both halves into the same folder - a team
 * that points at their SD card and picks "Termal" would otherwise ship 1.000
 * RGB frames into the thermal/ folder, which the sartname requires to be kept
 * separate.
 */
const THERMAL_SUFFIX = /_T\.[A-Za-z0-9]+$/i;

export function classifyVariant(name: string): DataKind {
  return THERMAL_SUFFIX.test(name) ? "thermal" : "rgb";
}

export function splitByVariant(
  files: File[],
  kind: DataKind
): { matching: File[]; mismatched: File[] } {
  const matching: File[] = [];
  const mismatched: File[] = [];
  for (const f of files) (classifyVariant(f.name) === kind ? matching : mismatched).push(f);
  return { matching, mismatched };
}

export function isAcceptedImage(file: File): boolean {
  return ACCEPTED.test(file.name);
}

export function filterImages(files: File[]): { accepted: File[]; rejected: File[] } {
  const accepted: File[] = [];
  const rejected: File[] = [];
  for (const f of files) (isAcceptedImage(f) ? accepted : rejected).push(f);
  return { accepted, rejected };
}

/**
 * Maps picked files onto blob paths.
 *
 * Names are de-duplicated because webkitdirectory happily returns the same file
 * name from different subfolders, and overwrite is blocked - a collision would
 * surface as a spurious "already exists" and silently drop an image.
 */
export function buildTasks(
  files: File[],
  form: TeamForm,
  prefix: string,
  sessionId: string
): FileTask[] {
  const folder = form.dataKind === "thermal" ? "thermal" : "rgb";
  const base = `${prefix}/${form.provinceSlug}/${sessionId}/${folder}`;
  const used = new Set<string>();

  return files.map((file, i) => {
    let name = safeFileName(file.name);
    if (used.has(name.toLowerCase())) {
      const dot = name.lastIndexOf(".");
      const stem = dot > 0 ? name.slice(0, dot) : name;
      const ext = dot > 0 ? name.slice(dot) : "";
      name = `${stem}_${String(i).padStart(4, "0")}${ext}`;
    }
    used.add(name.toLowerCase());

    return {
      id: i,
      file,
      key: fileKey(file),
      blobPath: `${base}/${name}`,
      status: "pending",
      loaded: 0,
      attempts: 0,
    };
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[u]}`;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "-";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h} sa ${m} dk`;
  if (m > 0) return `${m} dk ${s} sn`;
  return `${s} sn`;
}
